// React and related imports
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
// Material-UI imports
import { Stack, Box, Typography, IconButton, FormControlLabel, Checkbox, Paper, Tooltip } from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
// Third-party library imports
import { useFieldArray } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import noop from 'lodash/noop'
import { useDispatch, useSelector } from 'react-redux'
// Custom component imports
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import FreightPreferences from 'components/containers/customers/locations/FreightPreferences'
import PlacesAutocomplete from 'components/containers/customers/locations/PlacesAutocomplete'
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
// Utility imports
import { LOCATION_TYPES } from 'utils/constants'
import { getCountries, getCountryFromState } from 'utils/helpers'
import { ACCOUNT_TYPES } from 'utils/constants'
import { getLocationTypeChips } from './utils'
import StateProvinceInput from './StateProvinceInput'
import { useDeleteCustomerLocationMutation } from 'services/customers'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import { setSnackBar } from 'state/ui'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'

const locationTypesArray = [LOCATION_TYPES.CORPORATE, LOCATION_TYPES.SHIPPING, LOCATION_TYPES.PAY_TO]

const LocationsInformationForm = ({ control, errors, watch, setValue, getValues }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const { enableAddressPrintingUpdates, enableCountryCodes } = useSelector(state => state.featureFlags?.variables || {})
  const { country: yardCountry } = useSelector(st => st.settings.details)
  const { fields, prepend, remove } = useFieldArray({ control, name: 'locations' })
  const watchFieldArray = watch('locations')
  const controlledFields = fields.map((field, index) => ({ ...field, ...watchFieldArray[index] }))
  const countries = useMemo(() => getCountries(), [])
  const accountType = getValues('accountType')
  const isSupplier = [ACCOUNT_TYPES.SUPPLIER.id, ACCOUNT_TYPES.SUPPLIERANDCONSUMER.id].includes(accountType)
  const isConsumer = [ACCOUNT_TYPES.CONSUMER.id, ACCOUNT_TYPES.SUPPLIERANDCONSUMER.id].includes(accountType)
  const isSupplierAndConsumer = accountType === ACCOUNT_TYPES.SUPPLIERANDCONSUMER.id
  const [selectedLocation, setSelectedLocation] = useState(null)

  const [deleteCustomerLocation, { isLoading: isDeletingLocation }] = useDeleteCustomerLocationMutation()
  const dispatch = useDispatch()
  useGlobalLoader(isDeletingLocation)
  const { enableNewCustomerLocationDelete } = useSelector(state => state.featureFlags?.variables || {})

  const handleRowClick = params => {
    setSelectedLocation(prevId => (prevId === params.row.id ? null : params.row.id))
  }

  const handleDeleteLocation = async () => {
    const customerId = getValues('id')
    const locationId = selectedLocation
    await deleteCustomerLocation({ customerId, locationId })
      .unwrap()
      .then(() => {
        dispatch(
          setSnackBar({
            snackbarType: 'success',
            snackbarMessage: 'Customer location deleted successfully',
          }),
          setSelectedLocation(null),
        )
      })
      .catch(error => {
        const errorMsg = error?.data?.error || 'An error occurred while deleting the location'
        dispatch(
          setSnackBar({
            snackbarType: 'error',
            snackbarMessage: errorMsg,
          }),
        )
      })

    setIsDeleteModalOpen(false)
  }

  const handleRemoveLocation = locationItem => {
    if (enableNewCustomerLocationDelete) {
      setIsDeleteModalOpen(true)
      return
    }

    const contacts = getValues('contacts')
    const locationIndex = controlledFields.findIndex(field => field.id === locationItem)
    if (locationIndex === -1) return

    contacts.forEach((c, index) => {
      if (c.locationId === locationIndex.toString()) {
        setValue(`contacts.${index}.locationId`, '')
      } else if (c.locationId !== '' && parseInt(c.locationId, 10) > locationIndex) {
        setValue(`contacts.${index}.locationId`, (parseInt(c.locationId, 10) - 1).toString())
      }
    })

    remove(locationIndex)
    if (selectedLocation === locationItem) {
      setSelectedLocation(null)
    }
  }

  const handleAddLocation = () => {
    const newId = uuidv4()
    prepend({
      id: newId,
      name: '',
      locationType: '',
      locationTypes: [],
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: enableCountryCodes ? yardCountry : undefined,
      notes: '',
      manualAddress: false,
      coordinates: null,
    })
    setSelectedLocation(newId)
  }

  const handleChangeCountry = (locationIndex, newCountry) => {
    if (locationIndex !== -1) {
      setValue(`locations.${locationIndex}.country`, newCountry, { shouldDirty: true })
      const countryFromState = getCountryFromState(controlledFields[locationIndex].state)
      if (['US', 'CA'].includes(newCountry) && newCountry !== countryFromState) {
        // Need to reset state since US and CA have state dropdowns
        // Otherwise out of range state val won't show but will still be set
        setValue(`locations.${locationIndex}.state`, '', { shouldDirty: true })
      }
    }
  }

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Location',
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        renderCell: params => (
          <Tooltip title={params.row.name || ''} placement="left">
            <Typography
              noWrap
              sx={{
                fontWeight: 500,
              }}
            >
              {params.row.name || ''}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'address',
        headerName: 'Address',
        flex: 2,
        minWidth: 250,
        sortable: false,
        renderCell: params => {
          const { address1, city, state, zip } = params.row
          const addressDisplay = [address1, city, state, zip].filter(Boolean).join(', ') || ''
          return (
            <Tooltip title={addressDisplay} placement="left">
              <Typography
                noWrap
                color="text.secondary"
                sx={{
                  fontSize: '0.875rem',
                }}
              >
                {addressDisplay}
              </Typography>
            </Tooltip>
          )
        },
      },
      {
        field: 'types',
        headerName: 'Type',
        flex: 1.5,
        minWidth: 150,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: params => getLocationTypeChips(params.row, enableAddressPrintingUpdates),
      },
    ],
    [enableAddressPrintingUpdates],
  )

  const actionButtons = useMemo(
    () => [
      {
        id: 'add-new-location',
        text: 'Add New Location',
        onClick: handleAddLocation,
        icon: <Add />,
        variant: 'text',
        color: 'primary',
        size: 'medium',
        'data-testid': 'add-new-location',
      },
    ],
    [],
  )

  const renderEditForm = () => {
    if (!selectedLocation) return null

    const locationIndex = controlledFields.findIndex(field => field.id === selectedLocation)
    if (locationIndex === -1) return null

    const field = controlledFields[locationIndex]

    return (
      <Paper elevation={0} sx={{ mt: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box flex={1}>
              <Input
                key={`name-${selectedLocation}`}
                name={`locations.${locationIndex}.name`}
                label="Location Name"
                defaultValue=""
                control={control}
                error={!!errors.locations?.[locationIndex]?.name}
                helperText={errors.locations?.[locationIndex]?.name?.message}
                data-testid={`location-${locationIndex + 1}-name`}
              />
            </Box>
            <Box flex={1}>
              {enableAddressPrintingUpdates ? (
                <Select
                  key={`types-${selectedLocation}`}
                  name={`locations.${locationIndex}.locationTypes`}
                  label="Location Type"
                  defaultValue={[]}
                  control={control}
                  items={locationTypesArray}
                  error={!!errors.locations?.[locationIndex]?.locationTypes}
                  data-testid={`location-${locationIndex + 1}-type`}
                  selectProps={{
                    multiple: true,
                    sx: {
                      '.MuiSelect-select': {
                        padding: getValues(`locations.${locationIndex}.locationTypes`)?.length > 0 ? 1.5 : 2,
                      },
                    },
                  }}
                  useChips
                  useCheckboxes
                />
              ) : (
                <Select
                  key={`type-${selectedLocation}`}
                  name={`locations.${locationIndex}.locationType`}
                  label="Location Type"
                  defaultValue=""
                  control={control}
                  items={locationTypesArray}
                  error={!!errors.locations?.[locationIndex]?.locationType}
                  data-testid={`location-${locationIndex + 1}-type`}
                />
              )}
            </Box>
          </Stack>
          {enableCountryCodes && (
            <Box flex={1}>
              <Select
                key={`country-${selectedLocation}`}
                name={`locations.${locationIndex}.country`}
                label="Country"
                defaultValue=""
                control={control}
                items={countries}
                selectProps={{
                  onChange: e => {
                    handleChangeCountry(locationIndex, e.target.value)
                  },
                }}
                error={!!errors.locations?.[locationIndex]?.country}
                data-testid={`location-${locationIndex + 1}-country`}
              />
            </Box>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box flex={1}>
              <PlacesAutocomplete
                key={`places-${selectedLocation}`}
                onPlaceSelect={addressData => {
                  setValue(`locations.${locationIndex}.address1`, addressData.address1, { shouldDirty: true })
                  setValue(`locations.${locationIndex}.city`, addressData.city, { shouldDirty: true })
                  setValue(`locations.${locationIndex}.state`, addressData.state, { shouldDirty: true })
                  setValue(`locations.${locationIndex}.zip`, addressData.zip, { shouldDirty: true })
                  setValue(
                    `locations.${locationIndex}.coordinates`,
                    {
                      type: 'Point',
                      coordinates: [addressData.coordinates.lng, addressData.coordinates.lat],
                    },
                    { shouldDirty: true },
                  )
                }}
                index={locationIndex}
                disabled={watch(`locations.${locationIndex}.manualAddress`)}
                countries={field.country ? [field.country] : ['US', 'CA']}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  key={`manual-${selectedLocation}`}
                  checked={watch(`locations.${locationIndex}.manualAddress`) || false}
                  onChange={e => {
                    setValue(`locations.${locationIndex}.manualAddress`, e.target.checked, { shouldDirty: true })
                  }}
                  data-testid={`location-${locationIndex + 1}-manual-address`}
                />
              }
              label="Manually enter address"
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box flex={1}>
              <Input
                key={`address1-${selectedLocation}`}
                name={`locations.${locationIndex}.address1`}
                label="Address 1"
                defaultValue=""
                control={control}
                error={!!errors.locations?.[locationIndex]?.address1}
                helperText={errors.locations?.[locationIndex]?.address1?.message}
                data-testid={`location-${locationIndex + 1}-address1`}
                disabled={!watch(`locations.${locationIndex}.manualAddress`)}
              />
            </Box>
            <Box flex={1}>
              <Input
                key={`address2-${selectedLocation}`}
                name={`locations.${locationIndex}.address2`}
                label="Address 2"
                defaultValue=""
                control={control}
                error={!!errors.locations?.[locationIndex]?.address2}
                helperText={errors.locations?.[locationIndex]?.address2?.message}
                data-testid={`location-${locationIndex + 1}-address2`}
              />
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box flex={2}>
              <Input
                key={`city-${selectedLocation}`}
                name={`locations.${locationIndex}.city`}
                label="City"
                defaultValue=""
                control={control}
                error={!!errors.locations?.[locationIndex]?.city}
                helperText={errors.locations?.[locationIndex]?.city?.message}
                data-testid={`location-${locationIndex + 1}-city`}
                disabled={!watch(`locations.${locationIndex}.manualAddress`)}
              />
            </Box>
            <Box flex={1}>
              <StateProvinceInput
                key={`state-${selectedLocation}`}
                name={`locations.${locationIndex}.state`}
                defaultValue=""
                control={control}
                error={!!errors.locations?.[locationIndex]?.state}
                data-testid={`location-${locationIndex + 1}-state`}
                disabled={!watch(`locations.${locationIndex}.manualAddress`)}
                helperText={errors.locations?.[locationIndex]?.state?.message}
                enableCountryCodes={enableCountryCodes}
                country={field.country || getCountryFromState(field.state)}
              />
            </Box>
            <Box flex={1}>
              <Input
                key={`zip-${selectedLocation}`}
                name={`locations.${locationIndex}.zip`}
                label="Zip"
                defaultValue=""
                control={control}
                error={!!errors.locations?.[locationIndex]?.zip}
                helperText={errors.locations?.[locationIndex]?.zip?.message}
                data-testid={`location-${locationIndex + 1}-zip`}
                disabled={!watch(`locations.${locationIndex}.manualAddress`)}
              />
            </Box>
          </Stack>
          <Box>
            <Input
              key={`notes-${selectedLocation}`}
              name={`locations.${locationIndex}.notes`}
              label="Notes"
              defaultValue=""
              control={control}
              error={!!errors.locations?.[locationIndex]?.notes}
              helperText={errors.locations?.[locationIndex]?.notes?.message}
              data-testid={`location-${locationIndex + 1}-notes`}
              multiline
              rows={2}
            />
          </Box>
          <Box>
            <FreightPreferences
              key={`freight-${selectedLocation}`}
              accountTypes={{ isSupplier, isConsumer, isSupplierAndConsumer }}
              index={locationIndex}
              freightPricing={field.freightPricing}
            />
          </Box>
        </Stack>
      </Paper>
    )
  }

  return (
    <Stack flexGrow={1} sx={{ pl: 3, pr: 4 }}>
      <AlertModal
        open={isDeleteModalOpen}
        modalType="void"
        title="Delete Location"
        question="Are you sure you want to delete this location?"
        description="Deleting a location is irreversible."
        confirmLabel="YES, DELETE THIS LOCATION"
        cancelLabel="GO BACK"
        onClose={() => setIsDeleteModalOpen(false)}
        onSubmit={handleDeleteLocation}
      />
      <Typography variant="h6" color="textSecondary" data-testid="location-information-title">
        Locations
      </Typography>
      <Box py={1}>
        <Stack spacing={2}>
          <Typography variant="body2" color="textSecondary" data-testid="location-information-description">
            Add a new location or select an existing one to edit its details.
          </Typography>
          <DataGridWithSearchV2
            rows={controlledFields}
            columns={columns}
            textSearchPlaceholder="Search locations..."
            actionButtons={actionButtons}
            onRowClick={handleRowClick}
            disableColumnMenu
            disableColumnFilter
            disableColumnReorder
            hideFooterSelectedRowCount
            customPageSize={5}
            autoPageSize={false}
            searchBarSize="small"
            density="compact"
            getRowClassName={params => (params.id === selectedLocation ? 'Mui-selected' : '')}
            sx={{
              '& .MuiDataGrid-columnHeader': {
                paddingLeft: '16px',
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                },
              },
            }}
          />
          {selectedLocation && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                position: 'relative',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  color="textSecondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Edit {controlledFields.find(field => field.id === selectedLocation)?.name || 'Location'}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveLocation(selectedLocation)}
                  data-testid={`remove-location-${selectedLocation}`}
                >
                  <Delete />
                </IconButton>
              </Stack>
              {renderEditForm()}
            </Paper>
          )}
        </Stack>
      </Box>
    </Stack>
  )
}

LocationsInformationForm.propTypes = {
  control: PropTypes.object,
  errors: PropTypes.object,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  getValues: PropTypes.func,
}

LocationsInformationForm.defaultProps = {
  control: {},
  errors: {},
  watch: noop,
  setValue: noop,
  getValues: noop,
}

export default LocationsInformationForm
